import AppLayout from "../layouts/AppLayout";
import { Link } from "@inertiajs/react";
import { router } from "@inertiajs/react";
import {
  Users, ShieldAlert, TrendingUp, Calendar, AlertTriangle, Zap, Star, CheckCircle, Circle, Rocket, Activity, Send, Eye, MessageSquare, Sparkles,
} from "lucide-react";
import AskPermissionButton from "../components/AskPermissionButton";

interface Stats {
  total_candidates: number;
  active_pipeline: number;
  dnc_blocked: number;
  this_month: number;
}

interface DigestCandidate {
  id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
  pipeline_stage: string;
  priority: string;
  dnc_status: string;
  days_in_stage: number;
}

interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  href: string;
}

interface ExclusionReviewItem {
  id: number;
  action: "skipped_low_confidence" | "parse_failed_needs_review";
  firm_name: string | null;
  candidate_id: number;
  candidate_name: string;
  raw_email_snippet: string | null;
  created_at: string;
}

interface FirmReplyReviewItem {
  id: number;
  intent: string;
  confidence: string;
  reasoning: string;
  previous_status: string | null;
  new_status: string | null;
  suggested_status: string | null;
  target_label: string;
  target_href: string | null;
  snippet: string | null;
  created_at: string;
}

interface StrongMatchItem {
  job_match_id: number;
  candidate_id: number;
  candidate_name: string;
  candidate_first_name: string;
  candidate_email: string;
  job_id: number;
  firm_name: string;
  title: string;
  practice_area: string;
  tier: string;
  rationale: string | null;
  matched_at: string | null;
}

interface Props {
  stats: Stats;
  priority_candidates: DigestCandidate[];
  stale_candidates: DigestCandidate[];
  dnc_pending: number;
  new_alerts: number;
  new_alert_matches: number;
  skipped_jobs: number;
  outbound_pending: number;
  stage_counts: Record<string, number>;
  pipeline_thin: boolean;
  go_mode: boolean;
  onboarding: OnboardingStep[];
  exclusion_review_queue: ExclusionReviewItem[];
  reply_review_queue: FirmReplyReviewItem[];
  strong_match_queue: StrongMatchItem[];
  ai_assist: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead",
  intake_scheduled: "Intake Scheduled",
  resume_editing: "Resume Editing",
  package_pending: "Package Pending",
  submitted: "Submitted",
  skill_marketed: "Skill Marketed",
  screener: "Screener",
  full_round: "Full Round",
  offer_placed: "Offer / Placed",
};

const STAGE_ORDER = [
  "new_lead", "intake_scheduled", "resume_editing", "package_pending",
  "submitted", "skill_marketed", "screener", "full_round", "offer_placed",
];

const CONFIDENCE_COLOR: Record<string, string> = {
  high:   "text-green-600",
  medium: "text-amber-600",
  low:    "text-red-600",
};

const PRIORITY_TAG: Record<string, string> = {
  high:   "bg-red-100 text-red-700",
  normal: "bg-gray-100 text-gray-500",
  low:    "bg-blue-50 text-blue-600",
};

function CandidateRow({ c, showDays = false }: { c: DigestCandidate; showDays?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group">
      <div className="flex-1 min-w-0">
        <Link href={`/candidates/${c.id}`} className="text-sm font-medium text-gray-900 hover:text-green-700 truncate block">
          {c.full_name}
        </Link>
        <p className="text-xs text-gray-400 truncate">{c.firm_name}</p>
      </div>
      <div className="flex items-center gap-1.5 ml-3 shrink-0 flex-wrap justify-end">
        {c.practice_area && (
          <span className="text-xs bg-gray-50 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded">
            {c.practice_area}
          </span>
        )}
        <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_TAG[c.priority] ?? "bg-gray-100 text-gray-500"}`}>
          {c.priority}
        </span>
        <span className="text-xs text-gray-400">{STAGE_LABELS[c.pipeline_stage] ?? c.pipeline_stage}</span>
        {showDays && (
          <span className="text-xs font-medium text-amber-600 min-w-[2.5rem] text-right">{c.days_in_stage}d</span>
        )}
      </div>
    </div>
  );
}

function greetingForHour(hour: number): string {
  if (hour < 5) return "Burning the midnight oil";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard({ stats, priority_candidates, stale_candidates, dnc_pending, new_alerts, new_alert_matches, skipped_jobs, outbound_pending, stage_counts, pipeline_thin, go_mode, onboarding, exclusion_review_queue, reply_review_queue, strong_match_queue, ai_assist }: Props) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const greeting = greetingForHour(new Date().getHours());
  const onboardingDone = onboarding.every((s) => s.done);
  const onboardingComplete = onboarding.filter((s) => s.done).length;

  return (
    <AppLayout title="Dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{greeting}</h1>
        <p className="text-sm text-gray-400 mt-0.5">{today}</p>
      </div>

      {/* Go Mode banner */}
      {go_mode && (
        <div className="flex items-center gap-2 bg-green-900 text-green-100 text-sm px-4 py-2.5 rounded-lg mb-4">
          <Rocket className="w-4 h-4 shrink-0 text-green-300" />
          <span className="font-medium">Go Mode active</span>
          <span className="text-green-300 text-xs">— New candidates are auto-checked, cleared candidates get packages automatically.</span>
          <Link href="/settings" className="ml-auto text-xs font-medium text-green-300 hover:text-white">Settings</Link>
        </div>
      )}

      {/* Alert banners */}
      <div className="space-y-2 mb-6">
        {dnc_pending > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2.5 rounded-lg">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span><strong>{dnc_pending}</strong> candidate{dnc_pending !== 1 ? "s" : ""} in New Lead awaiting Exchange check</span>
            <button
              onClick={() => router.post("/candidates/queue_exchange_check_batch")}
              className="ml-auto text-xs font-medium hover:underline"
            >
              Run Checks
            </button>
          </div>
        )}
        {new_alerts > 0 && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-2.5 rounded-lg">
            <Zap className="w-4 h-4 shrink-0" />
            <span>
              <strong>{new_alerts}</strong> new Leopard alert{new_alerts !== 1 ? "s" : ""} in the last 24 hours
              {new_alert_matches > 0 && <> · <strong>{new_alert_matches}</strong> candidate match{new_alert_matches !== 1 ? "es" : ""}</>}
            </span>
            <Link href="/job-alerts" className="ml-auto text-xs font-medium hover:underline">View</Link>
          </div>
        )}
        {stale_candidates.length > 0 && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 text-sm px-4 py-2.5 rounded-lg">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span><strong>{stale_candidates.length}</strong> candidate{stale_candidates.length !== 1 ? "s" : ""} with no movement in 7+ days</span>
            <Link href="/pipeline" className="ml-auto text-xs font-medium hover:underline">Pipeline</Link>
          </div>
        )}
        {pipeline_thin && (
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 text-purple-800 text-sm px-4 py-2.5 rounded-lg">
            <Activity className="w-4 h-4 shrink-0" />
            <span>Pipeline looks thin — most active stages are empty</span>
            <Link href="/candidates/new" className="ml-auto text-xs font-medium hover:underline">Add candidate</Link>
          </div>
        )}
        {skipped_jobs > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2.5 rounded-lg">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span><strong>{skipped_jobs}</strong> job{skipped_jobs !== 1 ? "s" : ""} skipped — Leopard credentials not configured</span>
            <Link href="/settings" className="ml-auto text-xs font-medium hover:underline">Add credentials</Link>
          </div>
        )}
        {outbound_pending > 0 && (
          <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 text-sm px-4 py-2.5 rounded-lg">
            <Send className="w-4 h-4 shrink-0" />
            <span><strong>{outbound_pending}</strong> outbound list{outbound_pending !== 1 ? "s" : ""} pending action</span>
            <Link href="/outbound" className="ml-auto text-xs font-medium hover:underline">Review</Link>
          </div>
        )}
        {exclusion_review_queue.length > 0 && (
          <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-4 py-2.5 rounded-lg">
            <Eye className="w-4 h-4 shrink-0" />
            <span><strong>{exclusion_review_queue.length}</strong> possible firm exclusion{exclusion_review_queue.length !== 1 ? "s" : ""} need review</span>
          </div>
        )}
        {reply_review_queue.length > 0 && (
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm px-4 py-2.5 rounded-lg">
            <MessageSquare className="w-4 h-4 shrink-0" />
            <span><strong>{reply_review_queue.length}</strong> firm repl{reply_review_queue.length !== 1 ? "ies" : "y"} need{reply_review_queue.length === 1 ? "s" : ""} review</span>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<Users size={18} className="text-green-600" />} label="Total Candidates" value={stats.total_candidates} />
        <StatCard icon={<TrendingUp size={18} className="text-blue-600" />} label="Active Pipeline" value={stats.active_pipeline} />
        <StatCard icon={<ShieldAlert size={18} className="text-red-500" />} label="DNC Blocked" value={stats.dnc_blocked} />
        <StatCard icon={<Calendar size={18} className="text-purple-600" />} label="Added This Month" value={stats.this_month} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Action required */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-800">Priority — Needs Action</h2>
              </div>
              <Link href="/candidates?priority=high" className="text-xs text-green-700 hover:underline">View all</Link>
            </div>
            {priority_candidates.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">No high-priority candidates pending.</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {priority_candidates.map((c) => <CandidateRow key={c.id} c={c} showDays />)}
              </div>
            )}
          </div>

          {stale_candidates.length > 0 && (
            <div className="bg-white rounded-lg border border-orange-100">
              <div className="px-4 py-3 border-b border-orange-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <h2 className="text-sm font-semibold text-gray-800">No Movement</h2>
                </div>
                <Link href="/pipeline" className="text-xs text-green-700 hover:underline">Pipeline</Link>
              </div>
              <div className="divide-y divide-gray-50">
                {stale_candidates.map((c) => <CandidateRow key={c.id} c={c} showDays />)}
              </div>
            </div>
          )}
          {exclusion_review_queue.length > 0 && (
            <div className="bg-white rounded-lg border border-yellow-100">
              <div className="px-4 py-3 border-b border-yellow-100 flex items-center gap-2">
                <Eye className="w-4 h-4 text-yellow-500" />
                <h2 className="text-sm font-semibold text-gray-800">Possible Exclusions to Review</h2>
              </div>
              <div className="divide-y divide-gray-50">
                {exclusion_review_queue.map((item) => {
                  const parseFailed = item.action === "parse_failed_needs_review";
                  return (
                    <div key={item.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {parseFailed ? (
                            <p className="text-sm font-medium text-amber-700 flex items-center gap-1 truncate">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Couldn't parse — review raw email
                            </p>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 truncate">{item.firm_name}</p>
                          )}
                          <Link href={`/candidates/${item.candidate_id}`} className="text-xs text-gray-500 hover:text-green-700 truncate block">
                            {item.candidate_name}
                          </Link>
                          {item.raw_email_snippet && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2 italic">"{item.raw_email_snippet}"</p>
                          )}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          {!parseFailed && (
                            <button
                              onClick={() => router.post(`/candidate_exclusion_events/${item.id}/apply`)}
                              className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium"
                            >
                              Apply
                            </button>
                          )}
                          <button
                            onClick={() => router.delete(`/candidate_exclusion_events/${item.id}/dismiss`)}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:bg-gray-200"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {reply_review_queue.length > 0 && (
            <div className="bg-white rounded-lg border border-indigo-100">
              <div className="px-4 py-3 border-b border-indigo-100 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-indigo-500" />
                <h2 className="text-sm font-semibold text-gray-800">Firm Replies — Needs Review</h2>
              </div>
              <div className="divide-y divide-gray-50">
                {reply_review_queue.map((item) => (
                  <div key={item.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {item.target_href ? (
                          <Link href={item.target_href} className="text-sm font-medium text-gray-900 hover:text-green-700 truncate block">
                            {item.target_label}
                          </Link>
                        ) : (
                          <p className="text-sm font-medium text-gray-900 truncate">{item.target_label}</p>
                        )}
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <span className="text-xs text-gray-600 capitalize">{item.intent.replace(/_/g, " ")}</span>
                          <span className={`text-xs font-medium ${CONFIDENCE_COLOR[item.confidence] ?? "text-gray-500"}`}>
                            · {item.confidence}
                          </span>
                          {item.previous_status && (
                            <span className="text-xs text-gray-400">· was {item.previous_status.replace(/_/g, " ")}</span>
                          )}
                        </div>
                        {item.snippet && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2 italic">"{item.snippet}"</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0 items-end">
                        {item.suggested_status && (
                          <button
                            onClick={() => router.post(`/firm_reply_events/${item.id}/apply`)}
                            className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 font-medium whitespace-nowrap"
                          >
                            Apply → {item.suggested_status.replace(/_/g, " ")}
                          </button>
                        )}
                        <button
                          onClick={() => router.post(`/firm_reply_events/${item.id}/resolve`)}
                          className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 whitespace-nowrap"
                        >
                          Mark resolved
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New strong roster matches (task 2cdb320f, Goal 1) — matching runs
              automatically; this surfaces fresh strong fits so Steven can act. */}
          {strong_match_queue.length > 0 && (
            <div className="bg-white rounded-lg border border-green-100">
              <div className="px-4 py-3 border-b border-green-100 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-green-600" />
                <h2 className="text-sm font-semibold text-gray-800">New Strong Matches</h2>
                <span className="text-xs text-gray-400">— great fits for candidates you rep</span>
              </div>
              <div className="divide-y divide-gray-50">
                {strong_match_queue.map((item) => (
                  <div key={item.job_match_id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Link href={`/candidates/${item.candidate_id}`} className="text-sm font-medium text-gray-900 hover:text-green-700 truncate">
                            {item.candidate_name}
                          </Link>
                          <span className="text-xs text-gray-400">→</span>
                          <Link href={`/job-alerts/${item.job_id}`} className="text-sm text-gray-700 hover:text-green-700 truncate">
                            {item.firm_name}{item.title ? ` · ${item.title}` : ""}
                          </Link>
                        </div>
                        {item.rationale && (
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 italic">{item.rationale}</p>
                        )}
                      </div>
                      {ai_assist && item.candidate_email && (
                        <div className="shrink-0">
                          <AskPermissionButton
                            candidate={{ first_name: item.candidate_first_name, email: item.candidate_email }}
                            job={{ firm_name: item.firm_name, title: item.title, practice_area: item.practice_area }}
                            aiAssist={ai_assist}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pipeline stage summary */}
        <div className="bg-white rounded-lg border border-gray-200 h-fit">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">Pipeline Summary</h2>
            <Link href="/pipeline" className="text-xs text-green-700 hover:underline">Open</Link>
          </div>
          <div className="p-3 space-y-1.5">
            {STAGE_ORDER.map((stage) => {
              const count = stage_counts[stage] ?? 0;
              const max = Math.max(...STAGE_ORDER.map((s) => stage_counts[s] ?? 0), 1);
              return (
                <div key={stage}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-gray-600">{STAGE_LABELS[stage]}</span>
                    <span className="text-xs font-medium text-gray-800">{count}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 rounded-full transition-all"
                      style={{ width: `${count === 0 ? 0 : Math.max(4, Math.round((count / max) * 100))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Onboarding checklist — shown until all steps complete */}
      {!onboardingDone && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-green-600" />
              <h2 className="text-sm font-semibold text-gray-800">Getting Started</h2>
            </div>
            <span className="text-xs text-gray-400">{onboardingComplete} / {onboarding.length} complete</span>
          </div>
          <div className="divide-y divide-gray-50">
            {onboarding.map((step) => (
              <div key={step.key} className="flex items-center gap-3 px-4 py-3">
                {step.done
                  ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  : <Circle className="w-4 h-4 text-gray-300 shrink-0" />
                }
                <span className={`text-sm flex-1 ${step.done ? "line-through text-gray-400" : "text-gray-700"}`}>
                  {step.label}
                </span>
                {!step.done && (
                  <Link href={step.href} className="text-xs text-green-700 hover:underline font-medium">
                    Set up
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-5 py-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
    </div>
  );
}
